import { ImageSquareIcon } from '@phosphor-icons/react';
import React from 'react';

/**
 * The band a paid replica draws where the creative would be.
 *
 * A copy-only ad unit has no image, and a square `MediaSlot` for it costs
 * ~480px of empty grey to say one sentence — so both ad replicas collapse the
 * slot to this band instead. It lives in its own file because Meta and LinkedIn
 * were drawing the identical markup twice, and the moment the band gained a
 * second line the two copies would have drifted.
 *
 * `expectedFilename` is the whole reason the band is more than a shrug: naming
 * the file the slot is waiting for turns an empty state into an instruction and
 * publishes the convention downstream labelling depends on. It is optional and
 * must be omitted — never approximated — when the name cannot be built from
 * values that were actually stored.
 */
export const MissingCreativeBand: React.FC<{
  hint: string;
  expectedFilename?: string | null;
  /**
   * The way OUT of this state — normally a link to the surface that can put a
   * creative here. Optional because an exit sometimes genuinely does not exist,
   * and inventing a button that goes nowhere is worse than the empty state.
   * When there is no action, the caller's `hint` must say what the slot is
   * waiting for instead; a slot that only names an absence is the defect the
   * founder reported on 2026-08-27 ("there is no way for me to even attach a
   * creative there … so how do they actually show a creative there?").
   */
  action?: React.ReactNode;
  /**
   * The aspect ratio (width / height) reserved for the slot. Defaults to
   * 1.91:1, Facebook's landscape feed ratio — the ratio every existing
   * caller (LinkedIn ads, the pre-rebuild Meta ad) actually renders next to.
   * `MetaAdPreview` passes its own ~1:1 figure so the empty state reserves
   * the same footprint the Figma-matched full-bleed media now occupies,
   * rather than a narrower band that would make "no creative yet" look like
   * a smaller ad than a real one.
   */
  aspectRatio?: number;
  /**
   * 'dashed' (default) — the placeholder-slot grammar described above:
   * dashed inset, centred mark, filename line. Correct where a creative is
   * simply not filled in yet and the slot is inviting one.
   *
   * 'quiet' — flat `bg-muted/40`, no dashed border, no filename line. For a
   * paid preview whose format is deliberately text-only BY DESIGN (LinkedIn's
   * copy-only draft) rather than "not filled in yet" — a dashed "fill me in"
   * slot on a format that never takes an image reads as an unfinished field
   * instead of the true state.
   */
  variant?: 'dashed' | 'quiet';
}> = ({ hint, expectedFilename, action, aspectRatio = 1.91 / 1, variant = 'dashed' }) => {
  const filename = expectedFilename?.trim();

  if (variant === 'quiet') {
    return (
      <div
        style={{ aspectRatio }}
        className="flex flex-col items-center justify-center gap-1.5 bg-muted/40 p-3"
      >
        <ImageSquareIcon className="size-5 shrink-0 text-muted-foreground/50" weight="regular" />
        <p className="px-3 text-center text-detail text-muted-foreground">{hint}</p>
        {action ? <div className="mt-1">{action}</div> : null}
      </div>
    );
  }

  // A creative SLOT, not a void.
  //
  // The space reserved here is the space a real creative will occupy — the
  // founder's "it doesn't give me an actual idea of what the ad looks like"
  // is a request for that honesty. But an empty region that big reads as a
  // hole, so it is drawn as an explicit placeholder: dashed inset, centred
  // mark and label. Dashed-and-inset is this design system's established
  // grammar for "not real yet".
  //
  // Deliberately NOT a fixed 1:1 square for every caller — that cost ~480px
  // of empty grey on desktop for LinkedIn's 1.91:1 media; the `aspectRatio`
  // prop lets each caller reserve its own real footprint instead.
  return (
    <div style={{ aspectRatio }} className="bg-muted/30 p-3">
      <div className="flex size-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-border/60">
        <ImageSquareIcon className="size-5 shrink-0 text-muted-foreground/50" weight="regular" />
        <p className="px-3 text-center text-detail text-muted-foreground">{hint}</p>
        {filename ? (
          // No `break-all`: these names run long, and the browser's default
          // wrapping already breaks them after a hyphen, which keeps each line a
          // whole segment. `break-all` was measurably worse — it split `1x1` into
          // `1x` and `1`, turning the ratio into two meaningless fragments.
          <p className="px-3 text-center text-micro text-muted-foreground/70">
            Name it <span className="font-mono text-muted-foreground">{filename}</span>
          </p>
        ) : null}
        {action ? <div className="mt-1">{action}</div> : null}
      </div>
    </div>
  );
};
